/*istanbul ignore file */
import React, { useState, useMemo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import moment from "moment";
import { toast } from "react-toastify";
import { MODE, entityType, transactionType } from "../../constants/common";
import { PermissionEnum } from "../../constants/permissionService";
import ApiServices from "../../services/ApiServices";
import {
  ADD_AD,
  GET_AD,
  UPDATE_AD,
  EG_ADD_TO_CART,
  AD_COST_CALCULATE,
  AD_AVAILABILITY,
  AD_UNAVAILABLE_DATES,
  AD_COST_DELTA,
} from "../../constants/api-constants";
import { useLoading } from "../../hooks/useLoader";
import { useAuth } from "../../hooks/useAuth";
import { useMasterData } from "../../hooks/useMasterData";
import {
  TARGETING_FIELDS,
  EMPTY_TARGETING,
  CONTENT_REDIRECT_OPTIONS,
} from "./adManagementFormConfig";
import {
  buildAdManagementSchema,
  buildEditAdManagementSchema,
  ViewAdManagementSchema,
} from "./adManagementValidation";
import { INT_TO_CONTENT_REDIRECT } from "./components/ContentRedirectOption/ContentRedirectOption";
import {
  INT_TO_POSITION,
  POSITION_TO_INT,
  buildAdFormData,
  readFileAsDataUrl,
  deriveTargetDevices,
  deriveDevicePreviewUrls,
  deriveTargeting,
  resolveAdPageTitle,
  buildAllTouchedMap,
  countFilledTargetingFields,
  pickUploadedFile,
  buildCostCalculatePayload,
  expandUnavailableDateRanges,
  derivePurchaseFlags,
  deriveOverridePriceDraftLock,
  derivePermissionFlags,
} from "./components/Utils/adUtils";
import AdFormHeader from "./components/AdFormHeader";
import BasicInformationSection from "./components/BasicInformationSection";
import TargetingSection from "./components/TargetingSection";
import SchedulingSection from "./components/SchedulingSection";
import PurchasedLockNotice from "./components/PurchasedLockNotice";
import ConfirmActionDialog from "./components/ConfirmActionDialog";
import { isSuperAdminOrAdminRole } from "../../utilities/UserRoleUtils";

/* Frontend device key → backend numeric code (kept at module scope so it
 * doesn't churn memoisation deps inside the component). */
const DEVICE_TO_INT = { mobile: 1, tablet: 2, windows: 3 };

/* Submit-action identifiers passed through the form orchestrator.
 * Centralised here to avoid duplicated string literals (Sonar S1192). */
const SUBMIT_ACTIONS = Object.freeze({
  SAVE: "save",
  PUBLISH: "publish",
  APPLY: "apply",
  UPDATE: "update",
});

/* Parses an ISO date coming from the Ad GET API. Treats falsy values and
 * the .NET DateTime.MinValue placeholder ("0001-01-01T00:00:00") as
 * "no date" so the form behaves like a fresh Add for those fields. */
const parseAdDate = (value) => {
  if (!value) return null;
  const m = moment(value);
  if (!m.isValid() || m.year() < 1900) return null;
  return m;
};

/* Success-toast text keyed by scenario. Frozen so Sonar (S1192) doesn't
 * flag duplicated literals if a message is reused across branches. */
const SUCCESS_MESSAGES = Object.freeze({
  CREATED: "Ad created successfully.",
  UPDATED: "Ad updated successfully.",
  SAVED: "Ad saved successfully.",
  PUBLISHED: "Ad published successfully.",
  UPGRADED: "Ad upgraded successfully.",
});

/* Resolves the success-toast message purely from the button that triggered
 * the submit (plus role / mode context). This is intentionally NOT sourced
 * from the API response — BA wants a deterministic, button-driven message
 * regardless of what the backend returns in `responseStatus.description`. */
function resolveSuccessMessage({
  submitAction,
  isSuperAdmin,
  isAddMode,
  isPurchasedDistributorEdit,
}) {
  if (submitAction === SUBMIT_ACTIONS.APPLY) {
    return SUCCESS_MESSAGES.UPGRADED;
  }
  if (submitAction === SUBMIT_ACTIONS.UPDATE) {
    return SUCCESS_MESSAGES.UPDATED;
  }
  if (submitAction === SUBMIT_ACTIONS.PUBLISH) {
    if (isSuperAdmin) return SUCCESS_MESSAGES.PUBLISHED;
    if (isPurchasedDistributorEdit) return SUCCESS_MESSAGES.UPGRADED;
    return isAddMode ? SUCCESS_MESSAGES.CREATED : SUCCESS_MESSAGES.UPDATED;
  }
  /* SAVE (Save & Exit) */
  return isAddMode ? SUCCESS_MESSAGES.SAVED : SUCCESS_MESSAGES.UPDATED;
}

/* Resolves the contextual label for the primary action button. Extracted
 * to a pure helper to keep the component's cognitive complexity within
 * Sonar's threshold (S3776). */
function resolvePublishButtonLabel({
  isSuperAdmin,
  isSuperAdminEdit,
  isPurchased,
  isPurchasedDistributorEdit,
  hasUpgradeChanges,
  additionalCharge,
}) {
  if (isSuperAdminEdit && isPurchased) return "Update";
  if (isPurchasedDistributorEdit) {
    if (!hasUpgradeChanges) return "Update";
    return (additionalCharge ?? 0) > 0 ? "Pay & Apply" : "Apply Changes";
  }
  return isSuperAdmin ? "Publish" : "Pay & Publish";
}

/** Extracts the saved ad id from a successful submit response. */
function extractSavedAdId(response, fallbackAdId) {
  if (fallbackAdId) return fallbackAdId;
  const r = response?.result;
  if (typeof r === "number" || typeof r === "string") return r;
  return (
    r?.adId ?? r?.id ?? r?.bannerId ?? r?.AdId ?? r?.Id ?? r?.BannerId ?? null
  );
}

/** Handles non-success responses: either a slot-conflict or a generic error toast. */
function handleSubmitFailure(response, isUpdate, setAvailabilityInfo) {
  const nextAvailable =
    response?.result?.nextAvailableDate ??
    response?.result?.nextAvailableStartDate;
  if (nextAvailable) {
    setAvailabilityInfo?.({
      isAvailable: false,
      unavailableReason:
        response?.result?.unavailableReason ||
        response?.responseStatus?.error ||
        "Selected dates are not available.",
      nextAvailableDate: nextAvailable,
    });
    return;
  }
  const fallback = isUpdate ? "Failed to update ad." : "Failed to create ad.";
  toast.error(response?.responseStatus?.error || fallback);
}

/**
 * Submits an ad. Creates via POST /api/v1/Ad when `adId` is falsy,
 * updates via PUT /api/v1/Ad when `adId` is provided. Returns
 * `{ success, adId }`.
 */
async function submitAd({
  adId,
  values,
  startDate,
  endDate,
  targetingPayload,
  distributorId,
  deviceFiles,
  showLoader,
  currentUser,
  submitAction,
  isSuperAdmin,
  setAvailabilityInfo,
}) {
  const isUpdate = Boolean(adId);
  const isSuperAdminNoCartSubmit =
    isSuperAdmin &&
    (submitAction === SUBMIT_ACTIONS.PUBLISH ||
      submitAction === SUBMIT_ACTIONS.UPDATE);
  /* "apply" = purchased-ad upgrade with delta = $0. Save as Active (status 1)
   * exactly like publish, but skip the cart/Stripe step. */
  const keepActive =
    submitAction === SUBMIT_ACTIONS.PUBLISH ||
    submitAction === SUBMIT_ACTIONS.APPLY ||
    submitAction === SUBMIT_ACTIONS.UPDATE;
  const { showToAll, ...targeting } = targetingPayload;
  const formData = buildAdFormData({
    values,
    startDate,
    endDate,
    targeting,
    distributorId,
    deviceFiles,
    showToAll,
    currentUser,
    adId,
    status: keepActive ? 1 : 0,
  });
  if (isSuperAdminNoCartSubmit) {
    formData.append("SkipPayment", "true");
    formData.append("IsPublish", "true");
  }

  try {
    showLoader(true);
    setAvailabilityInfo?.(null);
    const submitter = isUpdate
      ? ApiServices.httpPutFile
      : ApiServices.httpPostFile;
    const endpoint = isUpdate ? UPDATE_AD : ADD_AD;
    const response = await submitter(endpoint, formData);

    if (response?.responseStatus?.isSuccess) {
      /* Success toast text is decided by the caller from the button that
       * triggered the submit — see `resolveSuccessMessage`. We deliberately
       * ignore `response.responseStatus.description` so messaging stays
       * deterministic and button-driven. */
      return {
        success: true,
        adId: extractSavedAdId(response, adId),
      };
    }
    handleSubmitFailure(response, isUpdate, setAvailabilityInfo);
  } catch (error) {
    const fallback = isUpdate
      ? "Failed to update ad. Please try again."
      : "Failed to create ad. Please try again.";
    toast.error(error?.message || fallback);
  } finally {
    showLoader(false);
  }
  return { success: false, adId: null, successMessage: null };
}

/**
 * Posts the ad to the Enterprise-Group cart so it can be paid for.
 * Triggered by the "Pay & Publish" button after the ad is saved/updated.
 */
async function addAdToCart(adId, { isUpgrade = false } = {}) {
  if (!adId) return false;
  try {
    const payload = {
      itemId: Number(adId),
      quantity: 1,
      transactionType: isUpgrade
        ? transactionType.AD_UPGRADE
        : transactionType.AD_PURCHASE,
      frequency: 99,
      currency: "USD",
      entityType: entityType.AD,
    };
    const response = await ApiServices.httpPost(EG_ADD_TO_CART, payload);
    if (response?.responseStatus?.isSuccess) return true;
    toast.error(response?.responseStatus?.error || "Failed to add ad to cart.");
  } catch (err) {
    toast.error(err?.message || "Failed to add ad to cart.");
  }
  return false;
}

/* ─── Module-level submit orchestrator ──────────────────────────────────── */
async function handleFormSubmit({
  values,
  startDate,
  endDate,
  targetingPayload,
  isAddMode,
  isEditMode,
  distributorId,
  deviceFiles,
  showLoader,
  onSave,
  adId,
  submitAction,
  currentUser,
  isSuperAdmin,
  setAvailabilityInfo,
  history,
  fetchedRecord,
  hasUpgradeChanges,
  isPurchasedDistributorEdit,
  additionalCharge,
}) {
  if (!isAddMode && !isEditMode) return;
  const isPublish = submitAction === SUBMIT_ACTIONS.PUBLISH;

  /* Step 1: create or update the ad */
  const { success, adId: savedAdId } = await submitAd({
    adId: isEditMode ? adId : null,
    values,
    startDate,
    endDate,
    targetingPayload,
    distributorId,
    deviceFiles,
    showLoader,
    currentUser,
    submitAction,
    isSuperAdmin,
    setAvailabilityInfo,
  });
  if (!success) return;

  /* Toast message is purely a function of the button + role + mode — never
   * sourced from the API response. */
  const successMessage = resolveSuccessMessage({
    submitAction,
    isSuperAdmin,
    isAddMode,
    isPurchasedDistributorEdit,
  });

  await handlePostSubmit({
    isPublish,
    isSuperAdmin,
    savedAdId,
    fetchedRecord,
    history,
    onSave,
    hasUpgradeChanges,
    isPurchasedDistributorEdit,
    additionalCharge,
    successMessage,
  });
}

/**
 * After the ad has been saved, decides whether to redirect to cart for
 * payment, add an upgrade to cart, or just exit back to the list.
 */
async function handlePostSubmit({
  isPublish,
  isSuperAdmin,
  savedAdId,
  fetchedRecord,
  history,
  onSave,
  hasUpgradeChanges,
  isPurchasedDistributorEdit,
  additionalCharge,
  successMessage,
}) {
  /* Helper: list-bound branches show the success toast; cart-redirect
   * branches stay silent. */
  const finishToList = () => {
    if (successMessage) toast.success(successMessage);
    onSave();
  };

  /* Super Admin publish path and Save & Exit both just close the form;
   * the backend has already marked the ad active in the super-admin case. */
  if (!isPublish || isSuperAdmin) {
    finishToList();
    return;
  }

  if (!savedAdId) {
    toast.error("Ad saved but unable to add to cart (missing ad id).");
    finishToList();
    return;
  }

  if (isPurchasedDistributorEdit) {
    const hasPayableUpgrade = hasUpgradeChanges && Number(additionalCharge) > 0;
    if (!hasPayableUpgrade) {
      finishToList();
      return;
    }
    history?.push("/cart/checkout");
    return;
  }

  const isUpgrade =
    fetchedRecord?.paymentStatus === 1 ||
    fetchedRecord?.paymentStatusText === "Purchased";
  const addedToCart = await addAdToCart(savedAdId, { isUpgrade });
  if (addedToCart) {
    history?.push("/cart/checkout");
  } else {
    finishToList();
  }
}

/*istanbul ignore next */
const AddEditAdManagement = (props) => {
  const {
    mode,
    adId,
    record: propsRecord,
    distributorId,
    isSuperAdmin,
    onCancel,
    onSave,
    onEdit,
    saveRef,
    dirtyRef,
    permissions,
  } = props;
  const { showLoader } = useLoading();
  const auth = useAuth();
  const currentUser = auth.getCurrentUser();
  const history = useHistory();

  const isViewMode = mode === MODE.VIEW;
  const isEditMode = mode === MODE.EDIT;
  const isAddMode = mode === MODE.ADD;

  /* ── Fetched record (loaded from GET /api/v1/Ad/{adId} or passed as prop) ── */
  const [fetchedRecord, setFetchedRecord] = useState(propsRecord ?? null);
  const [isFetching, setIsFetching] = useState(
    (isEditMode || isViewMode) && !propsRecord,
  );

  /* Derived: raw API record drives the form in edit/view, null in add */
  const existingRecord = isEditMode || isViewMode ? fetchedRecord : null;

  /* ── Fetch ad details when opening edit / view ───────────────────────── */
  useEffect(() => {
    if (!isEditMode && !isViewMode) return;
    if (!adId) return;
    if (propsRecord) return;
    let cancelled = false;
    setIsFetching(true);
    showLoader(true);
    ApiServices.httpGet(GET_AD(adId))
      .then((response) => {
        if (cancelled) return;
        const data = response?.result ?? response;
        setFetchedRecord(data);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load ad details.");
      })
      .finally(() => {
        if (!cancelled) {
          setIsFetching(false);
          showLoader(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adId]);

  /* ── Master data for targeting dropdowns ─────────────────────────────── */
  const { masterDataValue } = useMasterData();
  const masterData = useMemo(
    () => masterDataValue?.result ?? {},
    [masterDataValue],
  );

  const paymentStatusText =
    existingRecord?.paymentStatusText ?? existingRecord?.payment;
  const paymentStatusNumber = Number(existingRecord?.paymentStatus);
  const normalizedPaymentStatusText = String(paymentStatusText ?? "")
    .trim()
    .toLowerCase();
  const isPurchased =
    paymentStatusNumber === 1 || normalizedPaymentStatusText === "purchased";
  const isDraft =
    paymentStatusNumber === 0 || normalizedPaymentStatusText === "draft";
  const isAdminOrSuperAdmin = isSuperAdmin || isSuperAdminOrAdminRole();
  const { isLimited, isSuperAdminEdit, isFieldLocked } = derivePurchaseFlags({
    isEditMode,
    isPurchased,
    isSuperAdmin,
  });
  const isOverridePriceDraftLocked = deriveOverridePriceDraftLock({
    isEditMode,
    paymentStatus: existingRecord?.paymentStatus,
    paymentStatusText,
    discountedAmount: existingRecord?.discountedAmount,
    isAdminOrSuperAdmin,
  });
  const showDiscountedCost =
    !isPurchased &&
    ((isAddMode && isAdminOrSuperAdmin) || (!isAddMode && isDraft));
  const isSchedulingLocked = isFieldLocked || isOverridePriceDraftLocked;
  const isBasicMetadataLocked = isFieldLocked || isOverridePriceDraftLocked;
  const isPurchasedDistributorEdit = isLimited && !isSuperAdmin;
  /* Purchased + Edit (role-agnostic). When true the form is in "upgrade"
   * mode: skip availability / unavailable-dates / cost-calculate APIs
   * because dates and base cost are fixed from the original purchase —
   * only the delta API (AD_COST_DELTA) is relevant. */
  const isPurchasedEdit = isEditMode && isPurchased;

  const [superAdminConfirmOpen, setSuperAdminConfirmOpen] = useState(false);
  /* Availability state from POST /api/v1/Ad/availability — drives the
   * inline message under the End Date input. */
  const [availabilityInfo, setAvailabilityInfo] = useState(null);
  /* Dates returned by GET /api/v1/Ad/availability/unavailable-dates that
   * must be disabled in the date pickers. */
  const [unavailableDates, setUnavailableDates] = useState([]);

  /* Tracks which button triggered the submit: "save" | "publish" */
  const submitActionRef = useRef(SUBMIT_ACTIONS.SAVE);

  /* ── Formik holds the COMPLETE form state (basic info + targeting +
   *    showToAll + per-device files + preview URLs). `enableReinitialize`
   *    re-seeds the whole form once `fetchedRecord` resolves after GET,
   *    so the manual sync-effect that previously called setShowToAll /
   *    setTargeting / setDevicePreviewUrls is no longer needed. */
  const initialValues = useMemo(
    () => ({
      bannerName: existingRecord?.title ?? existingRecord?.bannerName ?? "",
      position:
        INT_TO_POSITION[existingRecord?.bannerPosition] ??
        existingRecord?.position ??
        "",
      startDate: parseAdDate(existingRecord?.startDate),
      endDate: parseAdDate(existingRecord?.endDate),
      targetDevices: deriveTargetDevices(existingRecord),
      ContentRedirectType:
        INT_TO_CONTENT_REDIRECT[existingRecord?.contentRedirectType] ??
        existingRecord?.ContentRedirectType ??
        (isAddMode ? (CONTENT_REDIRECT_OPTIONS[0]?.value ?? "") : ""),
      externalUrl: existingRecord?.externalUrl ?? "",
      linkedContentIds: existingRecord?.linkedContentIds ?? [],
      /* Full selection objects ({ tab, id, name, data }) kept so the
       * SelectContentDialog can restore checkboxes when the user clicks
       * "Update Content". `linkedContentIds` (integers only) is still the
       * field used for validation + API payload.
       *
       * Edit-mode hydration: the GET ad response only returns
       * `linkedContentIds` (numbers, no tab info), so we synthesise stub
       * entries here. The dialog handles `tab`-less entries by promoting
       * them to the correct tab as records load. */
      linkedContentItems:
        existingRecord?.linkedContentItems ??
        (existingRecord?.linkedContentIds ?? []).map((id) => ({ id })),
      /* Consolidated fields (previously useState) */
      showToAll: existingRecord
        ? (existingRecord.showToAllSubscribers ?? false)
        : true,
      targeting: existingRecord
        ? deriveTargeting(existingRecord)
        : { ...EMPTY_TARGETING },
      deviceFiles: {},
      devicePreviewUrls: existingRecord
        ? deriveDevicePreviewUrls(existingRecord)
        : {},
      discountedAmount: existingRecord?.discountedAmount ?? null,
    }),
    [existingRecord, isAddMode],
  );

  const [estimatedCost, setEstimatedCost] = useState(0);
  const [isCalculatingCost, setIsCalculatingCost] = useState(false);
  const costTimerRef = useRef(null);

  const activeValidationSchema = useMemo(() => {
    if (isViewMode) return ViewAdManagementSchema;
    if (isEditMode) {
      return buildEditAdManagementSchema(estimatedCost, showDiscountedCost);
    }
    return buildAdManagementSchema(estimatedCost, showDiscountedCost);
  }, [isViewMode, isEditMode, estimatedCost, showDiscountedCost]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema: activeValidationSchema,
    onSubmit: async (values) => {
      const {
        showToAll,
        targeting,
        deviceFiles,
        devicePreviewUrls: _previewUrls,
        linkedContentItems: _linkedItems,
        ...basicValues
      } = values;
      await handleFormSubmit({
        values: basicValues,
        startDate: values.startDate,
        endDate: values.endDate,
        targetingPayload: { showToAll, ...targeting },
        isAddMode,
        isEditMode,
        distributorId,
        deviceFiles,
        showLoader,
        onSave,
        adId,
        submitAction: submitActionRef.current,
        currentUser,
        isSuperAdmin,
        setAvailabilityInfo,
        history,
        fetchedRecord,
        hasUpgradeChanges,
        isPurchasedDistributorEdit,
        additionalCharge,
      });
    },
  });

  useEffect(() => {
    if (isViewMode) return;
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeValidationSchema, isViewMode]);

  /* ── Targeting dropdown options (depend on selected country) ─────────── */
  const targetingOptions = useMemo(() => {
    const toOpts = (arr, valueKey, labelKey = "name") =>
      (arr ?? []).map((item) => ({
        value: item[valueKey] ?? item.value,
        label: item[labelKey] ?? item.text ?? item.name ?? item.label,
      }));

    const selectedCountries = Array.isArray(formik.values.targeting?.country)
      ? formik.values.targeting.country
      : [];
    const allStates = masterData.states ?? [];
    const filteredStates =
      selectedCountries.length > 0
        ? allStates.filter((s) => selectedCountries.includes(s.countryId))
        : allStates;

    return {
      subscriberType: toOpts(masterData.subscriberTypes, "subscriberTypeId"),
      carriersRepresented: toOpts(
        masterData.carrierRepresents,
        "carrierRepresentId",
      ),
      majorAreas: toOpts(masterData.areas, "areaId"),
      numberOfProducers: [],
      industryGroups: toOpts(masterData.industryGroups, "industryGroupId"),
      technologyBeingUsed: toOpts(masterData.technologies, "technologyId"),
      yearsInBusiness: [],
      annualRevenue: Array.isArray(masterData.annualRevenues)
        ? masterData.annualRevenues.map((item) => ({
            value: item.annualRevenueId,
            label: item.name,
          }))
        : [],
      numberOfEmployees: Array.isArray(masterData.numberOfEmployees)
        ? masterData.numberOfEmployees.map((item) => ({
            value: item.value,
            label: item.text,
          }))
        : [],
      numberOfPrincipals: [],
      country: toOpts(masterData.countries, "countryId"),
      state: filteredStates.map((s) => ({ value: s.stateId, label: s.name })),
      city: [],
      zip: [],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterData, formik.values.targeting]);

  /* ── Computed values ─────────────────────────────────────────────────── */
  const appliedFieldCount = useMemo(
    () =>
      countFilledTargetingFields(
        formik.values.targeting,
        formik.values.showToAll,
        TARGETING_FIELDS,
      ),
    [formik.values.targeting, formik.values.showToAll],
  );

  const targetingMultiplier = useMemo(
    () => Math.min(2.0, 1.0 + appliedFieldCount * 0.1),
    [appliedFieldCount],
  );

  /* Devices that were already saved on the ad — used to lock them as
   * non-removable when a regular admin edits a purchased ad. Recomputed
   * only when the underlying record changes. */
  const lockedDevices = useMemo(
    () => (isFieldLocked ? deriveTargetDevices(existingRecord) : []),
    [isFieldLocked, existingRecord],
  );

  /* ── Purchased-ad upgrade delta ──────────────────────────────────────
   * For a Distributor editing a Purchased + Active ad: any newly-added
   * Target Devices and any newly-filled Targeting fields drive a delta
   * cost from POST /api/v1/Ad/cost/delta. Banner Name / image / re-saving
   * existing values are non-billable (delta = 0). */

  /* Originally purchased baseline (devices + targeting) — used to detect
   * which devices / fields the distributor newly added. */
  const originalDevices = useMemo(
    () => deriveTargetDevices(existingRecord),
    [existingRecord],
  );
  const originalTargeting = useMemo(
    () => (existingRecord ? deriveTargeting(existingRecord) : EMPTY_TARGETING),
    [existingRecord],
  );
  const originalShowToAll = existingRecord?.showToAllSubscribers ?? true;

  /* Devices the distributor added on top of the purchased set (codes). */
  const newTargetDevices = useMemo(() => {
    const current = formik.values.targetDevices ?? [];
    return current
      .filter((d) => !originalDevices.includes(d))
      .map((d) => DEVICE_TO_INT[d] ?? 0)
      .filter(Boolean);
  }, [formik.values.targetDevices, originalDevices]);

  /* For delta API payload: send ALL currently-selected devices (not only
   * newly-added ones), as requested by updated backend behavior. */
  const selectedTargetDevicesForDelta = useMemo(
    () =>
      (formik.values.targetDevices ?? [])
        .map((d) => DEVICE_TO_INT[d] ?? 0)
        .filter(Boolean),
    [formik.values.targetDevices],
  );

  /* Targeting fields newly filled (were empty before, have value now).
   * When the ad was originally "Show to all subscribers" any field that
   * the distributor now fills counts as new. */
  const newFilledTargetingFieldCount = useMemo(() => {
    if (formik.values.showToAll) return 0;
    const isFilled = (val) =>
      Array.isArray(val) ? val.length > 0 : Boolean(val);
    const baseline = originalShowToAll ? EMPTY_TARGETING : originalTargeting;
    return TARGETING_FIELDS.filter((f) => {
      const now = formik.values.targeting?.[f.key];
      const before = baseline?.[f.key];
      return isFilled(now) && !isFilled(before);
    }).length;
  }, [
    formik.values.targeting,
    formik.values.showToAll,
    originalTargeting,
    originalShowToAll,
  ]);

  /* For delta API payload: when ShowToAll is false, send the TOTAL filled
   * targeting field count (not only newly-added targeting fields). */
  const totalFilledTargetingFieldCount = useMemo(
    () =>
      countFilledTargetingFields(
        formik.values.targeting,
        formik.values.showToAll,
        TARGETING_FIELDS,
      ),
    [formik.values.targeting, formik.values.showToAll],
  );

  const hasUpgradeChanges =
    newTargetDevices.length > 0 || newFilledTargetingFieldCount > 0;

  const [additionalCharge, setAdditionalCharge] = useState(0);
  const [isCalculatingDelta, setIsCalculatingDelta] = useState(false);
  const deltaTimerRef = useRef(null);
  useEffect(() => {
    /* Purchased upgrade mode (any role): only the delta API is relevant. */
    if (!isPurchasedEdit) {
      setAdditionalCharge(0);
      setIsCalculatingDelta(false);
      return undefined;
    }
    if (!adId) return undefined;
    if (!hasUpgradeChanges) {
      setAdditionalCharge(0);
      setIsCalculatingDelta(false);
      return undefined;
    }
    if (deltaTimerRef.current) clearTimeout(deltaTimerRef.current);
    setIsCalculatingDelta(true);
    deltaTimerRef.current = setTimeout(async () => {
      try {
        const payload = {
          adId: Number(adId),
          newTargetDevices: selectedTargetDevicesForDelta,
          newFilledTargetingFieldCount: totalFilledTargetingFieldCount,
        };
        const res = await ApiServices.httpPost(AD_COST_DELTA, payload);
        if (res?.responseStatus?.isSuccess) {
          setAdditionalCharge(
            res.result?.additionalCharge ??
              res.result?.deltaCost ??
              res.result?.estimatedCost ??
              0,
          );
        }
      } catch (_) {
        /* keep previous value on network error */
      } finally {
        setIsCalculatingDelta(false);
      }
    }, 500);
    return () => {
      if (deltaTimerRef.current) clearTimeout(deltaTimerRef.current);
    };
  }, [
    isPurchasedEdit,
    adId,
    hasUpgradeChanges,
    selectedTargetDevicesForDelta,
    totalFilledTargetingFieldCount,
  ]);

  /* estimatedCost — fetched from POST /api/v1/Ad/cost/calculate.
   * Debounced 500 ms: the timer resets on every change so only ONE call fires
   * 500 ms after the user stops editing, regardless of how many fields changed.
   *
   * Purchased-ad edit (distributor): skip the calculate API entirely — the
   * base cost is already known from the existing record and any incremental
   * charge is computed via the AD_COST_DELTA effect above. */
  useEffect(() => {
    /* Purchased-ad edit (any role): seed the displayed cost from the saved
     * record and skip the calculate API. AD_COST_DELTA handles the
     * incremental charge. */
    if (isPurchasedEdit) {
      const originalCost =
        existingRecord?.amount ??
        existingRecord?.estimatedCost ??
        existingRecord?.amountCharged ??
        existingRecord?.cost ??
        existingRecord?.totalAmount ??
        0;
      setEstimatedCost(Number(originalCost) || 0);
      setIsCalculatingCost(false);
      return undefined;
    }
    const {
      startDate,
      endDate,
      position,
      targetDevices,
      showToAll,
      targeting,
    } = formik.values;
    if (!startDate || !endDate) {
      setEstimatedCost(0);
      setIsCalculatingCost(false);
      return undefined;
    }
    if (costTimerRef.current) clearTimeout(costTimerRef.current);
    setIsCalculatingCost(true);
    costTimerRef.current = setTimeout(async () => {
      try {
        const payload = buildCostCalculatePayload({
          distributorId,
          position,
          startDate,
          endDate,
          targetDevices,
          showToAll,
          targeting,
          filledTargetingFieldCount: appliedFieldCount,
        });
        const res = await ApiServices.httpPost(AD_COST_CALCULATE, payload);
        if (res?.responseStatus?.isSuccess) {
          setEstimatedCost(res.result?.estimatedCost ?? 0);
        }
      } catch (_) {
        /* keep previous value on network error */
      } finally {
        setIsCalculatingCost(false);
      }
    }, 500);
    return () => {
      if (costTimerRef.current) clearTimeout(costTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.startDate,
    formik.values.endDate,
    formik.values.position,
    formik.values.targetDevices,
    formik.values.targeting,
    formik.values.showToAll,
    appliedFieldCount,
    distributorId,
    isPurchasedEdit,
    existingRecord,
  ]);

  /* ── Unavailable dates for the selected banner position ────────────────
   * Fetched once per (distributorId, position) change. Returns an array of
   * date strings that the date pickers will disable. */
  useEffect(() => {
    const position = formik.values.position;
    /* Purchased upgrade mode: dates are fixed; skip the lookup. */
    const skipUnavailableDates = !position || isPurchasedEdit;
    if (skipUnavailableDates) {
      setUnavailableDates([]);
      return;
    }
    let cancelled = false;
    const payload = {
      bannerPosition: POSITION_TO_INT[position] ?? 1,
      excludeAdId: Number(adId) || 0,
    };
    ApiServices.httpPost(AD_UNAVAILABLE_DATES, payload)
      .then((res) => {
        if (cancelled) return;
        setUnavailableDates(expandUnavailableDateRanges(res?.result));
      })
      .catch(() => {
        if (!cancelled) setUnavailableDates([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.position, adId]);

  /* ── Availability check ────────────────────────────────────────────────
   * Debounced 500 ms. Mirrors the cost-calculate effect's trigger set.
   * Stores the response on `availabilityInfo` so SchedulingSection can
   * render the inline message below the End Date input. */
  const availabilityTimerRef = useRef(null);
  useEffect(() => {
    const { startDate, endDate, position } = formik.values;
    /* Purchased upgrade mode: availability is irrelevant — the slot is
     * already owned by this ad. */
    const skipAvailability =
      !startDate || !endDate || !position || isPurchasedEdit;
    if (skipAvailability) {
      setAvailabilityInfo(null);
      return undefined;
    }
    if (availabilityTimerRef.current) {
      clearTimeout(availabilityTimerRef.current);
    }
    availabilityTimerRef.current = setTimeout(async () => {
      try {
        const payload = {
          bannerPosition: POSITION_TO_INT[position] ?? 1,
          startDate: moment(startDate).format("YYYY-MM-DD"),
          endDate: moment(endDate).format("YYYY-MM-DD"),
          excludeAdId: Number(adId) || 0,
          excludeUserId: Number(currentUser?.userId) || 0,
        };
        const res = await ApiServices.httpPost(AD_AVAILABILITY, payload);
        if (res?.responseStatus?.isSuccess && res?.result) {
          setAvailabilityInfo(res.result);
        } else {
          setAvailabilityInfo(null);
        }
      } catch (err) {
        /* Network/parse failure: clear availability info so stale data
         * doesn't block the user. */
        setAvailabilityInfo(null);
      }
    }, 500);
    return () => {
      if (availabilityTimerRef.current) {
        clearTimeout(availabilityTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.startDate,
    formik.values.endDate,
    formik.values.position,
    adId,
  ]);

  const remainingDays = useMemo(() => {
    if (!formik.values.startDate || !formik.values.endDate) return 0;
    return Math.max(
      1,
      moment(formik.values.endDate).diff(
        moment(formik.values.startDate),
        "days",
      ) + 1,
    );
  }, [formik.values.startDate, formik.values.endDate]);

  const contentRedirectLabel = useMemo(
    () =>
      CONTENT_REDIRECT_OPTIONS.find(
        (opt) => opt.value === formik.values.ContentRedirectType,
      )?.label ?? "",
    [formik.values.ContentRedirectType],
  );

  /* ── Handlers (all route writes through Formik) ──────────────────────── */
  const handleDeviceFileSelect = (device) => (fileOrEvent) => {
    /* The cropper calls this with a File directly; FileUploader calls it with an event */
    const file = pickUploadedFile(fileOrEvent);
    if (!file) return;
    formik.setFieldValue(`deviceFiles.${device}`, file);
    readFileAsDataUrl(file, (dataUrl) =>
      formik.setFieldValue(`devicePreviewUrls.${device}`, dataUrl),
    );
  };

  const handleDeviceFileDelete = (device) => () => {
    formik.setFieldValue(`deviceFiles.${device}`, null);
    formik.setFieldValue(`devicePreviewUrls.${device}`, null);
  };

  const handleTargetingChange = (key, value) => {
    formik.setFieldValue(`targeting.${key}`, value);
  };

  const handleSetShowToAll = (value) => {
    formik.setFieldValue("showToAll", value);
  };

  const handlePositionChange = (event) => {
    const nextPosition = event.target.value;
    if (nextPosition === formik.values.position) return;
    formik.setFieldValue("position", nextPosition);
    formik.setFieldValue("deviceFiles", {});
    formik.setFieldValue("devicePreviewUrls", {});
  };

  const handleDeviceToggle = (deviceValue, checked) => {
    /* On purchased ads (regular admin only) the originally-saved devices
     * cannot be removed. Allow adding new devices, block unchecking the
     * locked ones. Super admin bypasses this via `isFieldLocked = false`. */
    if (
      isFieldLocked &&
      !checked &&
      (lockedDevices || []).includes(deviceValue)
    ) {
      return;
    }
    const current = formik.values.targetDevices;
    const updated = checked
      ? [...current, deviceValue]
      : current.filter((d) => d !== deviceValue);
    formik.setFieldValue("targetDevices", updated);
  };

  /* ── Button-enable state ─────────────────────────────────────────────
   * `formik.isValid` already covers the device-image rule via the schema
   * test in `adManagementValidation.js`, so both Save & Exit and
   * Pay & Publish enable as soon as every required field (including a
   * file/preview URL for each selected device) is satisfied.
   *
   * Permission gating (`permissions` prop is an array of feature names
   * like ["Create", "Update", "List"]):
   *   - ADD  mode → requires Create
   *   - EDIT mode → requires Update
   *   - VIEW mode → Edit button only shown when Update is granted */
  const {
    canSave: rawCanSave,
    canPublish: rawCanPublish,
    editHandler,
  } = derivePermissionFlags({
    permissions,
    isAddMode,
    isViewMode,
    formikIsValid: formik.isValid,
    isLimited,
    onEdit,
    createPerm: PermissionEnum.CREATE,
    updatePerm: PermissionEnum.UPDATE,
    isSuperAdmin,
    isPurchasedDistributorEdit,
  });

  /* Save & Exit keeps the original edit behavior: it stays disabled until
   * the user actually changes something. Draft edit mode only bypasses
   * this dirty check for the Publish / Pay & Publish action when the form
   * is already valid. Cancel and the View-mode Edit button are unaffected.
   *
   * We can't rely on `formik.dirty` here because Formik's deep-equality
   * (react-fast-compare) sees two distinct moment instances representing
   * the same date as different objects, and several child components
   * write benign normalized values back into the form on mount — both
   * flip `dirty` to true without any user action. Comparing the JSON
   * serialisation of values vs initialValues sidesteps both problems
   * (moment#toJSON() yields the same ISO string for the same instant). */
  const isUserDirty = useMemo(
    () =>
      JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues),
    [formik.values, formik.initialValues],
  );
  const saveDirtyGate = !isEditMode || isUserDirty;
  const publishDirtyGate = !isEditMode || isDraft || isUserDirty;
  const canSave = rawCanSave && saveDirtyGate;
  const canPublish = rawCanPublish && publishDirtyGate;

  /* ── Derived display values ──────────────────────────────────────────── */
  const pageTitle = resolveAdPageTitle({ isViewMode, isEditMode });
  const paymentBadgeColor = isPurchased ? "success" : "default";
  const paymentLabel = existingRecord?.paymentStatusText || "Draft";
  /* Action button label:
   *   - Purchased + Active edit (distributor):
   *       • no device / targeting additions ............. "Update"
   *       • additions, but delta = $0 (same tier) ......... "Apply Changes"
   *       • additions, delta > $0 ......................... "Pay & Apply"
   *   - Super Admin ..................................... "Publish"
   *   - Everyone else ................................... "Pay & Publish" */
  const publishButtonLabel = resolvePublishButtonLabel({
    isSuperAdmin,
    isSuperAdminEdit,
    isPurchased,
    isPurchasedDistributorEdit,
    hasUpgradeChanges,
    additionalCharge,
  });
  /* When a purchased ad is being edited by a distributor or super admin
   * we collapse the toolbar to that single contextual button — Save & Exit
   * isn't used in these flows. */
  const hideSaveExit =
    isPurchasedDistributorEdit || (isSuperAdminEdit && isPurchased);

  const handleSubmitAction = async (action) => {
    const normalizedAction =
      isSuperAdminEdit && isPurchased && action === SUBMIT_ACTIONS.PUBLISH
        ? SUBMIT_ACTIONS.UPDATE
        : action;
    submitActionRef.current = normalizedAction;
    if (isSuperAdminEdit && existingRecord?.paymentStatus === 1) {
      setSuperAdminConfirmOpen(true);
      return;
    }
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(buildAllTouchedMap(formik.values));
      toast.error("Please fix all required fields before saving.");
      return;
    }
    formik.handleSubmit();
  };

  /* Expose save trigger + dirty state to parent */
  if (saveRef) saveRef.current = () => handleSubmitAction(SUBMIT_ACTIONS.SAVE);
  if (dirtyRef) dirtyRef.current = isUserDirty;

  /* Browser-level unsaved-changes guard (refresh / close / back).
   * In-app navigation is already covered by `page.setOnDirtyNavigation`
   * in AdManagementList; this only fires when the browser would unload
   * the page entirely. Listener is attached only while dirty. */
  useEffect(() => {
    if (!isUserDirty) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isUserDirty]);

  /* Don't render form until API data is loaded (global loader is active) */
  if (isFetching) return null;

  /* Accordion shared sx */
  const accordionSx = {
    mb: 2,
    border: "1px solid #e0e0e0",
    boxShadow: "none",
    borderRadius: "6px !important",
    "&:before": { display: "none" },
    "& .MuiAccordionSummary-root": { backgroundColor: "#f4f4f4 !important" },
    "& .MuiAccordionSummary-expandIconWrapper": { color: "#1aaefa" },
  };

  /* ─────────────────────────────────────────────────────────────────────── */
  return (
    <Box sx={{ px: 2, py: 1 }}>
      <AdFormHeader
        pageTitle={pageTitle}
        isViewMode={isViewMode}
        isEditMode={isEditMode}
        paymentLabel={paymentLabel}
        paymentBadgeColor={paymentBadgeColor}
        isLimited={isLimited}
        publishButtonLabel={publishButtonLabel}
        hideSaveExit={hideSaveExit}
        onCancel={onCancel}
        onSubmitAction={handleSubmitAction}
        onEdit={editHandler}
        canSave={canSave}
        canPublish={canPublish}
      />

      <form
        onSubmit={formik.handleSubmit}
        noValidate
        style={{ height: "calc(100vh - 400px)", overflow: "auto" }}
      >
        <BasicInformationSection
          formik={formik}
          isViewMode={isViewMode}
          isEditMode={isEditMode}
          isLimited={isBasicMetadataLocked}
          areTargetDevicesLocked={isOverridePriceDraftLocked}
          lockedDevices={lockedDevices}
          devicePreviewUrls={formik.values.devicePreviewUrls}
          deviceFiles={formik.values.deviceFiles}
          existingRecord={existingRecord}
          paymentLabel={paymentLabel}
          onPositionChange={handlePositionChange}
          onDeviceToggle={handleDeviceToggle}
          onDeviceFileSelect={handleDeviceFileSelect}
          onDeviceFileDelete={handleDeviceFileDelete}
          accordionSx={accordionSx}
        />

        <TargetingSection
          showToAll={formik.values.showToAll}
          setShowToAll={handleSetShowToAll}
          targeting={formik.values.targeting}
          targetingOptions={targetingOptions}
          targetingMultiplier={targetingMultiplier}
          appliedFieldCount={appliedFieldCount}
          isViewMode={isViewMode}
          isLimited={isOverridePriceDraftLocked}
          handleTargetingChange={handleTargetingChange}
          accordionSx={accordionSx}
        />

        <SchedulingSection
          formik={formik}
          isViewMode={isViewMode}
          isLimited={isSchedulingLocked}
          estimatedCost={estimatedCost}
          isCalculatingCost={isCalculatingCost}
          additionalCharge={additionalCharge}
          isCalculatingDelta={isCalculatingDelta}
          showAdditionalCharge={isPurchasedEdit}
          showDiscountedCost={showDiscountedCost}
          remainingDays={remainingDays}
          contentRedirectLabel={contentRedirectLabel}
          distributorId={distributorId}
          unavailableDates={unavailableDates}
          availabilityInfo={availabilityInfo}
          accordionSx={accordionSx}
        />

        <PurchasedLockNotice isLimited={isLimited && !isSuperAdmin} />
      </form>

      <ConfirmActionDialog
        open={superAdminConfirmOpen}
        title="Apply Changes?"
        message="Do you want to apply these changes? This will update the live ad immediately."
        onCancel={() => setSuperAdminConfirmOpen(false)}
        onConfirm={() => {
          setSuperAdminConfirmOpen(false);
          formik.submitForm();
        }}
      />
    </Box>
  );
};

AddEditAdManagement.propTypes = {
  mode: PropTypes.string.isRequired,
  adId: PropTypes.number,
  record: PropTypes.object,
  distributorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSuperAdmin: PropTypes.bool,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  onEdit: PropTypes.func,
  saveRef: PropTypes.object,
  dirtyRef: PropTypes.object,
  permissions: PropTypes.arrayOf(PropTypes.string),
};

AddEditAdManagement.defaultProps = {
  adId: null,
  record: null,
  distributorId: null,
  isSuperAdmin: false,
  onCancel: () => {},
  onSave: () => {},
  onEdit: null,
  permissions: [],
};

export default AddEditAdManagement;
